import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import LocaleSelector from "@/shared/components/LocaleSelector";
import { usePermissions } from "@/shared/hooks/usePermissions";
import type { NavItem } from "./types";

interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The 4 top-level navigation menus, keyed by section. Each section's
   * `title` becomes a collapsible group header in the drawer; its items
   * become `<Link>`s inside.
   */
  sections: { id: string; title: string; items: NavItem[] }[];
}

/**
 * Slide-in mobile navigation drawer (visible below `md`). Renders the
 * Operations / Administration / Tools / System menus as expandable
 * sections so users on phones / portrait tablets can still reach every
 * page that the desktop menubar exposes.
 *
 * The drawer is also where the locale selector lives at narrow widths —
 * it's hidden from the navbar's right-side cluster below `md` (gap
 * pressure) and surfaced here instead.
 */
const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  isOpen,
  onClose,
  sections,
}) => {
  const location = useLocation();
  const { hasPermission, hasPermissions } = usePermissions();
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  // User-controlled openSection — `null` means "use the fallback (first
  // section)". The fallback is computed at render time so it self-heals
  // if `sections` arrives empty on the first render and is populated on
  // a later one (e.g. async permission load).
  const [openSection, setOpenSection] = React.useState<string | null>(null);
  const effectiveOpenSection = openSection ?? sections[0]?.id ?? null;

  // Close on Escape; prevent body scroll while open. Capture-and-
  // restore the previous body.overflow value so closing the drawer
  // doesn't clobber another component's overflow lock (e.g. another
  // modal opened on top).
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus into the drawer for screen-reader users — the close
    // button is the safest landing target since it's keyboard-reachable
    // and labelled.
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isItemAllowed = (item: NavItem): boolean => {
    if (item.allowWithoutPermission) return true;
    if (item.permission && !hasPermission(item.permission)) return false;
    // OR logic — second arg is `requireAll`, leaving it false (the default).
    if (item.permissions && !hasPermissions(item.permissions)) return false;
    return true;
  };

  const renderItem = (item: NavItem, depth = 0): React.ReactNode => {
    if (!isItemAllowed(item)) return null;
    if (item.divider) {
      return <hr key={item.key} className="my-2 border-border" />;
    }

    // Item with submenu — render as a nested expandable group (depth+1).
    if (item.submenu && item.submenu.length > 0) {
      return (
        <details key={item.key} className="group" open={depth === 0}>
          <summary
            className="flex items-center justify-between px-3 py-2 text-sm font-medium text-text cursor-pointer rounded-md hover:bg-surface-alt"
            style={{ paddingLeft: `${0.75 + depth * 1}rem` }}
          >
            <span>{item.label ?? item.key}</span>
            <ChevronRight
              size={16}
              className="text-text-muted transition-transform group-open:rotate-90"
              aria-hidden="true"
            />
          </summary>
          <ul className="ml-2 border-l border-border">
            {item.submenu.map((sub) => renderItem(sub, depth + 1))}
          </ul>
        </details>
      );
    }

    const isActive = location.pathname === item.to;
    const Icon = item.icon;
    return (
      <li key={item.key}>
        <Link
          to={item.to}
          onClick={onClose}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-surface-alt ${
            isActive ? "bg-primary/10 text-primary font-medium" : "text-text"
          }`}
          style={{ paddingLeft: `${0.75 + depth * 1}rem` }}
          {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {Icon ? (
            <Icon size={16} className="shrink-0 text-text-muted" aria-hidden="true" />
          ) : null}
          <span>{item.label ?? item.key}</span>
        </Link>
      </li>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[220] md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
      id="mobile-nav-drawer"
    >
      {/* Backdrop — clicking outside the drawer closes it. */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel — slides in from the left, takes ~85% width to
          leave room for users to tap the backdrop and dismiss. */}
      <aside
        className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-surface shadow-xl flex flex-col animate-in slide-in-from-left duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-text">Menu</h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded p-1 text-text-muted hover:text-text"
            onClick={onClose}
            aria-label="Close navigation menu"
            data-testid="mobile-nav-close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <nav className="flex-1 overflow-y-auto p-2" aria-label="Mobile navigation">
          {sections.map((section) => {
            const isOpen2 = effectiveOpenSection === section.id;
            return (
              <div key={section.id} className="mb-1">
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-text-strong hover:bg-surface-alt"
                  onClick={() =>
                    setOpenSection((prev) => (prev === section.id ? null : section.id))
                  }
                  aria-expanded={isOpen2}
                  aria-controls={`mobile-nav-section-${section.id}`}
                >
                  <span>{section.title}</span>
                  <ChevronDown
                    size={16}
                    className={`text-text-muted transition-transform ${isOpen2 ? "" : "-rotate-90"}`}
                    aria-hidden="true"
                  />
                </button>
                {isOpen2 ? (
                  <ul
                    id={`mobile-nav-section-${section.id}`}
                    className="mt-1 mb-2"
                  >
                    {section.items.map((item) => renderItem(item))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </nav>

        {/* Locale selector lives in the drawer at narrow widths because
            it's hidden from the navbar's right cluster below `md`. */}
        <footer className="border-t border-border px-4 py-3">
          <LocaleSelector />
        </footer>
      </aside>
    </div>
  );
};

export default MobileNavDrawer;
